import React, { useMemo, useState } from 'react';
import ThemeToggle from './ThemeToggle';

const Login = () => {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [magicMessage, setMagicMessage] = useState('');
  const [magicFound, setMagicFound] = useState(null);
  const [magicLink, setMagicLink] = useState('');
  const [showLocal, setShowLocal] = useState(false);
  const params = new URLSearchParams(window.location.search);
  const nextParam = params.get('next') || '/';
  const next = useMemo(() => encodeURIComponent(nextParam), [nextParam]);
  const ssoError = params.get('sso') === 'error';
  const ssoReason = params.get('reason');
  const magicStatus = params.get('magic');

  const onRequestLogin = async (e) => {
    e.preventDefault();
    setError('');
    setMagicMessage('');
    setMagicFound(null);
    setMagicLink('');
    setSubmitting(true);
    try {
      const resp = await fetch('/api/auth/request-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setError(data.error || 'Something went wrong. Please try again.');
        return;
      }
      setMagicFound(!!data.found);
      setMagicMessage(data.message || (data.found ? 'Check your email for a sign-in link.' : 'No account found for that email.'));
      if (data.magicLink) setMagicLink(data.magicLink);
    } catch (err) {
      setError(err.message || 'Request failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-6 px-4">
      <div className="absolute top-4 right-4"><ThemeToggle /></div>

      <div className="w-full max-w-sm bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-700">
        <div className="px-8 pt-6 pb-5">
          <div className="flex flex-col items-center mb-4">
            <img src="/images/context-networks-logo.png" alt="Context Networks" className="w-[320px] h-auto max-w-full max-h-40 sm:max-h-48 object-contain mb-2" />
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Order Insertion</h1>
          </div>

          <h2 className="text-base font-semibold text-center text-gray-900 dark:text-white mb-4">Login</h2>

          {ssoError && (
            <div className="mb-3 p-2.5 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm text-center">
              SSO sign-in failed. Try again or use User Login.
            </div>
          )}
          {ssoReason && (
            <div className="-mt-2 mb-3 text-[11px] text-center text-gray-500 dark:text-gray-400">
              Debug reason: {decodeURIComponent(ssoReason)}
            </div>
          )}
          {magicStatus && magicStatus !== 'ok' && (
            <div className="mb-3 p-2.5 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm text-center">
              {magicStatus === 'expired' && 'That sign-in link has expired. Request a new one below.'}
              {magicStatus === 'invalid' && 'That sign-in link is invalid. Request a new one below.'}
              {magicStatus === 'disabled' && 'This account has been disabled. Contact an administrator.'}
              {magicStatus === 'error' && 'Sign-in failed. Please try again.'}
            </div>
          )}

          <div className="space-y-3">
            <a href={`/api/auth/azure/login?next=${next}`} className="w-full inline-flex items-center justify-center px-4 py-2.5 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-white rounded-lg font-medium shadow-sm">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 mr-2"><path d="M11 11H3V3h8v8zm10-8h-8v8h8V3zM3 13h8v8H3v-8zm10 0h8v8h-8v-8z"/></svg>
              Sign in with Azure SSO
            </a>
            <p className="text-[11px] text-center text-gray-500 dark:text-gray-400 -mt-1">For Context Networks staff</p>

            <div className="relative my-2">
              <div className="absolute inset-0 flex items-center" aria-hidden="true">
                <div className="w-full border-t border-gray-200 dark:border-gray-700" />
              </div>
              <div className="relative flex justify-center">
                <span className="px-2 bg-white dark:bg-gray-800 text-xs text-gray-500">OR</span>
              </div>
            </div>

            <button type="button" onClick={() => setShowLocal((v)=>!v)} className="w-full inline-flex items-center justify-center px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium shadow">
              User Login
            </button>

            {showLocal && (
              <form onSubmit={onRequestLogin} className="pt-2">
                <div className="grid gap-2">
                  <input type="email" className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} required />
                  {error && <div className="text-sm text-red-600 dark:text-red-400">{error}</div>}
                  {magicMessage && (
                    <div className={`text-sm ${magicFound ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}`}>
                      {magicMessage}
                    </div>
                  )}
                  {magicLink && (
                    <a
                      href={magicLink}
                      className="text-sm text-blue-600 dark:text-blue-400 underline break-all"
                    >
                      Open local sign-in link
                    </a>
                  )}
                  <button type="submit" disabled={submitting} className="w-full inline-flex items-center justify-center px-4 py-2 bg-gray-900 dark:bg-gray-700 hover:bg-gray-800 dark:hover:bg-gray-600 text-white rounded-lg font-medium shadow disabled:opacity-60">
                    {submitting ? 'Sending…' : 'Email me a sign-in link'}
                  </button>
                </div>
              </form>
            )}

            <div className="text-center pt-2 text-[11px] text-gray-500 dark:text-gray-400">
              Need help? Email
              {' '}
              <a href="mailto:advertising@contextnetworks.net" className="underline">advertising@contextnetworks.net</a>
            </div>
          </div>

          <div className="mt-6 text-center text-[11px] text-gray-500 dark:text-gray-400">© 2025 Context Networks, Inc. All rights reserved.</div>
        </div>
      </div>
    </div>
  );
};

export default Login;
